import useSWR from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import {
  type IReasonGroupOption,
  type IRightsSectionOtsukuReasonGroupResponse,
} from "../types";

interface IUseRightsSectionOtsukuReasonOptionsReturnType {
  reasonOptions: IReasonGroupOption[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * 権利部（乙区）検索の登記原因プルダウンの選択肢を取得するカスタムフック
 *
 * valueには検索APIのreason_group_idに渡す区分IDを、labelには画面に表示する名称を設定する。
 * categoryはプルダウン内の見出し表示に使用する。
 *
 * マスタの追加・削除を画面の再表示で反映するため、マウントのたびに再取得する
 * （キャッシュがあれば表示しつつ裏で更新）。フォーカス復帰時の再取得は、入力中に
 * 選択肢が入れ替わるのを避けるため行わない。
 */
export const useRightsSectionOtsukuReasonOptions =
  (): IUseRightsSectionOtsukuReasonOptionsReturnType => {
    const { apiClient } = useApiClient();

    const fetcher = async (
      url: string
    ): Promise<IRightsSectionOtsukuReasonGroupResponse> => {
      const { data } =
        await apiClient.get<IRightsSectionOtsukuReasonGroupResponse>(url);
      return data;
    };

    const toOptionList = (
      data: IRightsSectionOtsukuReasonGroupResponse | undefined
    ): IReasonGroupOption[] => {
      if (data === undefined) return [];

      return data.list.map((group) => ({
        value: group.id.toString(),
        label: group.name,
        category: group.category,
      }));
    };

    const { data, error, isLoading } = useSWR<
      IRightsSectionOtsukuReasonGroupResponse,
      Error
    >("option/rights-section-otsuku/reason", fetcher, {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    });

    return {
      reasonOptions: toOptionList(data),
      isLoading,
      isError: Boolean(error),
    };
  };
